import { jsPDF } from 'jspdf';
import { TeacherAssessmentDetail, TeacherAssessmentSection } from '../types';

/**
 * Build a printable assessment PDF, client-side (jsPDF) — same approach as the
 * session plan download in LessonPlanner.
 *
 * Two flavours from the same sections:
 *  - question paper: questions only, with ruled space for written answers
 *  - answer key: correct MCQ option + explanation, or model answer + rubric
 */

const VIOLET: [number, number, number] = [124, 58, 237];
const INK: [number, number, number] = [15, 23, 42];
const MUTED: [number, number, number] = [100, 116, 139];
const RULE: [number, number, number] = [203, 213, 225];
const EMERALD: [number, number, number] = [4, 120, 87];

const M = 40; // page margin
const INDENT = 16; // everything that hangs under a question stem

/** Answer-key annotations under a question stem. */
const KEY = { size: 10, color: EMERALD, indent: INDENT, lead: 13 };
const HINT = { size: 9.5, color: MUTED, indent: INDENT, lead: 12 };

const buildAssessmentPdf = (
  detail: TeacherAssessmentDetail,
  sections: TeacherAssessmentSection[],
  { includeAnswers }: { includeAnswers: boolean }
): jsPDF => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - M * 2;
  let y = M + 6;

  const ensure = (need: number) => {
    if (y + need > pageH - M) {
      doc.addPage();
      y = M + 6;
    }
  };

  interface TextOpts {
    size?: number;
    bold?: boolean;
    color?: [number, number, number];
    indent?: number;
    lead?: number;
    gap?: number;
  }

  interface Block {
    lines: string[];
    height: number;
    opts: Required<TextOpts>;
  }

  /**
   * Wrap a string to the content width once. Callers sum `height` to decide
   * where to break the page, then `draw` the same `lines` — so the height a
   * block was measured at is always the height it renders at.
   */
  const layout = (value: string, opts: TextOpts = {}): Block => {
    const resolved: Required<TextOpts> = {
      size: 11,
      bold: false,
      color: INK,
      indent: 0,
      lead: 15,
      gap: 0,
      ...opts,
    };
    doc.setFont('helvetica', resolved.bold ? 'bold' : 'normal');
    doc.setFontSize(resolved.size);
    const lines = value ? (doc.splitTextToSize(value, contentW - resolved.indent) as string[]) : [];
    return {
      lines,
      height: lines.length ? lines.length * resolved.lead + resolved.gap : 0,
      opts: resolved,
    };
  };

  const draw = ({ lines, opts }: Block) => {
    if (lines.length === 0) return;
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setFontSize(opts.size);
    doc.setTextColor(...opts.color);
    lines.forEach((ln) => {
      ensure(opts.lead);
      doc.text(ln, M + opts.indent, y);
      y += opts.lead;
    });
    y += opts.gap;
  };

  const text = (value: string, opts: TextOpts = {}) => draw(layout(value, opts));

  const sectionHeading = (label: string) => {
    ensure(34);
    y += 6;
    text(label.toUpperCase(), { size: 9, bold: true, color: VIOLET, lead: 13, gap: 3 });
  };

  /** Ruled lines for a hand-written answer. */
  const answerLines = (count: number) => {
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.5);
    for (let i = 0; i < count; i += 1) {
      ensure(18);
      y += 12;
      doc.line(M + 14, y, pageW - M, y);
      y += 6;
    }
    y += 4;
  };

  // ── Cover block ────────────────────────────────────────────────────────────
  const kind = includeAnswers ? 'ANSWER KEY' : 'ASSESSMENT';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...VIOLET);
  doc.text(`CURIOSITY COACH · ${kind}`, M, y);
  y += 20;

  text(detail.title || 'Assessment', { size: 20, bold: true, lead: 24 });

  const topicNames = (detail.topics || []).map((t) => t.topic_title).filter(Boolean);
  const totalQuestions = sections.reduce((n, s) => n + s.questions.length, 0);
  const meta = [
    `Grade ${detail.grade}`,
    `${totalQuestions} question${totalQuestions !== 1 ? 's' : ''}`,
    topicNames.join(', '),
  ]
    .filter(Boolean)
    .join(' · ');
  text(meta, { size: 10, color: MUTED, lead: 13, gap: 8 });

  if (!includeAnswers) {
    // Name / date / score strip so the paper can be handed out as-is.
    ensure(30);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.5);
    doc.line(M, y, pageW - M, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('Name: ______________________________', M, y);
    doc.text('Date: ______________', M + contentW * 0.55, y);
    doc.text('Score: ______', pageW - M - 60, y);
    y += 10;
    doc.line(M, y, pageW - M, y);
    y += 18;
  } else {
    y += 4;
  }

  // ── Questions ──────────────────────────────────────────────────────────────
  let n = 0; // continuous numbering, so the key lines up with the paper
  sections.forEach((section, si) => {
    sectionHeading(`Section ${si + 1} · ${section.title}`);

    section.questions.forEach((q) => {
      n += 1;

      // Lay the whole question out first so it can be kept on one page — a
      // question must never be split from its options or its answer.
      const blocks: Block[] = [layout(`${n}.  ${q.question}`, { bold: true, gap: 2 })];

      if (q.kind === 'mcq' && q.options?.length) {
        q.options.forEach((opt) => {
          const isCorrect = includeAnswers && q.correct_option_id === opt.id;
          blocks.push(
            // Helvetica in jsPDF is WinAnsi-encoded, so stick to latin-1 glyphs.
            layout(`${opt.id.toUpperCase()})  ${opt.text}${isCorrect ? '   (correct)' : ''}`, {
              size: 10.5,
              bold: isCorrect,
              color: isCorrect ? EMERALD : INK,
              indent: INDENT,
              lead: 14,
            })
          );
        });
        if (includeAnswers) {
          blocks.push(
            layout(`Answer: ${(q.correct_option_id || '-').toUpperCase()}`, { ...KEY, bold: true })
          );
          if (q.explanation) blocks.push(layout(`Why: ${q.explanation}`, HINT));
        }
      } else if (includeAnswers) {
        if (q.model_answer) blocks.push(layout(`Model answer: ${q.model_answer}`, KEY));
        if (q.rubric) blocks.push(layout(`Look for: ${q.rubric}`, HINT));
      }

      const rules = !includeAnswers && q.kind !== 'mcq' ? 3 : 0;
      const need = blocks.reduce((h, b) => h + b.height, 0) + rules * 18;
      ensure(Math.min(need, pageH - M * 2));

      blocks.forEach(draw);
      if (rules) answerLines(rules);
      y += 8;
    });
  });

  // ── Footer on every page ───────────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `${detail.title || 'Assessment'}${includeAnswers ? ' · Answer key' : ''}`,
      M,
      pageH - 22
    );
    doc.text(`Page ${p} of ${pages}`, pageW - M, pageH - 22, { align: 'right' });
  }

  return doc;
};

export const downloadAssessmentPdf = (
  detail: TeacherAssessmentDetail,
  sections: TeacherAssessmentSection[],
  opts: { includeAnswers: boolean }
): void => {
  const doc = buildAssessmentPdf(detail, sections, opts);
  const slug =
    (detail.title || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() ||
    'assessment';
  doc.save(`${slug}${opts.includeAnswers ? '-answer-key' : ''}.pdf`);
};
